import React, { Fragment, useEffect, useState, useCallback } from 'react';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import { H3, P } from '../../../Themes/Components/AbstractElements';
import ReactApexChart from 'react-apexcharts';
import DataTable from 'react-data-table-component';
import { ArrowUp, ArrowDown } from 'react-feather';
import { formatDistanceToNow } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import Loading from '../../../Themes/Components/Loading';
import ConfiguracaoUsuariosService from '../../../Services/Configuracao/ConfiguracaoUsuariosService';
import { toast } from 'react-toastify';

const EventosDetalheChart = ({ data, configId }) => {
  const [tabelaData, setTabelaData] = useState({ rows: [], total: 0 });
  const [tableLoading, setTableLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [dadosFormatados, setDadosFormatados] = useState({
    series: [],
    options: {
      labels: [],
    },
  });

  useEffect(() => {
    if (data && data.data) {
      const valores = data.data.data.reduce((acc, val, i) => {
        if (val > 0) acc.push(i);
        return acc;
      }, []);

      const series = valores.map((i) => data.data.data[i]);
      const labels = valores.map((i) => data.data.labels[i]);
      const colors = valores.map((i) => data.data.colors[i]);

      const dados = {
        series,
        options: {
          labels,
          colors: colors,
          plotOptions: {
            pie: {
              dataLabels: {
                offset: -10,
                minAngleToShowLabel: 10,
              },
            },
          },
          dataLabels: {
            enabled: true,
            formatter: function (val, opts) {
              const value = opts.w.globals.series[opts.seriesIndex];
              return value + ' (' + val.toFixed(1) + '%)';
            },
          },
          legend: {
            position: 'bottom',
          },
          responsive: [
            {
              breakpoint: 480,
              options: {
                chart: {
                  width: 200,
                },
                legend: {
                  position: 'bottom',
                },
              },
            },
          ],
        },
      };

      setDadosFormatados(dados);
    }
  }, [data]);

  const fetchTabela = useCallback(
    async (page) => {
      if (!data?._grafico) return;

      setTableLoading(true);
      try {
        const response = await ConfiguracaoUsuariosService.getGrafico(data._grafico, configId, { page });

        setTabelaData({
          rows: response.tabela || [],
          total: response.paginacao?.total ?? response.tabela?.length ?? 0,
        });

        setCurrentPage(page);
      } catch (error) {
        toast.error('Erro ao carregar os dados da tabela.');
      } finally {
        setTableLoading(false);
      }
    },
    [data, configId],
  );

  useEffect(() => {
    if (data?.tabela) {
      setTabelaData({
        rows: data.tabela,
        total: data.paginacao?.total ?? data.tabela.length,
      });
    }
  }, [data]);

  const columns = [
    {
      name: '',
      width: '48px',
      center: true,
      cell: (row) =>
        row.dentro_prazo ? <ArrowUp size={18} color='#22BAA0' /> : <ArrowDown size={18} color='#f25656' />,
    },
    {
      name: 'Tempo',
      center: true,
      cell: (row) => {
        if (!row.tempo) return '-';
        const date = new Date(row.tempo);
        if (isNaN(date.getTime())) {
          return '-';
        }
        return formatDistanceToNow(date, { locale: ptBR, addSuffix: false });
      },
    },
    {
      name: 'Número',
      center: true,
      selector: (row) => row.numero,
    },
    {
      name: <div style={{ whiteSpace: 'normal', textAlign: 'center' }}>Ref. Cliente</div>,
      center: true,
      selector: (row) => row.ref_cliente || '-',
    },
    {
      name: <div style={{ whiteSpace: 'normal', textAlign: 'center' }}>Responsável pelo Atraso</div>,
      center: true,
      selector: (row) => row.responsavel_atraso || '-',
    },
    {
      name: <div style={{ whiteSpace: 'normal', textAlign: 'center' }}>Justificativa do Atraso</div>,
      grow: 2,
      center: true,
      selector: (row) => row.justificativa_atraso || '-',
    },
  ];

  const semRegistros = dadosFormatados.series.every((v) => v === 0);

  return (
    <Fragment>
      <Card>
        <CardHeader className='pb-0'>
          <H3>{data?.titulo}</H3>
        </CardHeader>
        <CardBody>
          {semRegistros ? (
            <p style={{ textAlign: 'center', fontSize: '14px', color: 'black' }}>Nenhum registro encontrado.</p>
          ) : (
            <Row>
              <Col md='4' className='d-flex align-items-center'>
                <div className='medium-chart'>
                  <div className='recent-chart' id='recent-chart'>
                    <ReactApexChart
                      options={dadosFormatados.options}
                      series={dadosFormatados.series}
                      type='pie'
                      height={355}
                    />
                  </div>
                </div>
              </Col>
              <Col md='8'>
                <DataTable
                  columns={columns}
                  data={tabelaData.rows}
                  striped
                  responsive
                  highlightOnHover
                  progressPending={tableLoading}
                  progressComponent={<Loading />}
                  pagination
                  paginationServer
                  paginationPerPage={10}
                  paginationTotalRows={tabelaData.total}
                  paginationDefaultPage={currentPage}
                  paginationComponentOptions={{
                    noRowsPerPage: true,
                    rangeSeparatorText: 'de',
                  }}
                  onChangePage={fetchTabela}
                />
              </Col>
            </Row>
          )}
        </CardBody>
      </Card>
    </Fragment>
  );
};

export default EventosDetalheChart;
